import { PageWrapper } from '@components'
import { LABELS, TOOLTIPS, URL_PATHS, WARNINGS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useSetPageTitle } from '@hooks/useSetPageTitle'
import { useStore } from '@hooks/useStore'
import { Add, Search } from '@mui/icons-material'
import { Box, Button, Tooltip } from '@mui/material'
import {
  GridCallbackDetails,
  GridRow,
  GridRowParams,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import { IUiModalGeneric } from '@typings'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { OvDataGrid } from 'src/components/core/ov-data-grid/ov-data-grid'
import { POLICIES } from 'src/constants/policies.constants'
import { FindCompanyForm, MobileClientRow } from 'src/partials/index.partials'

export const ClientsList = observer(() => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])
  const { client, ui, policies } = useStore()
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  const handleRowClick = (params: GridRowParams) => {
    if (!params.id) return
    const path = generatePath(URL_PATHS.CLIENT_DETAIL, {
      id: params.id,
    })
    return navigate(path)
  }

  const canCreate = useMemo(() => {
    return policies.can(POLICIES.CLIENT.CREATE)
  }, [policies.current_policies])

  const newClientModalOptions: Partial<IUiModalGeneric> = {
    title: 'Zoek in KVK',
    body: () => <FindCompanyForm callBack={row => client.createClient(row)} />,
    modalOptions: {
      fullWidth: true,
    },
  }

  const handleRowSelection = (model: GridRowSelectionModel, details: GridCallbackDetails) => setRowSelectionModel(model)

  const handleCreateClient = () => {
    navigate(URL_PATHS.CLIENT_CREATE)
  }

  const handleSearchInKvk = async () => {
    await ui.showModal(newClientModalOptions).catch(() => void 0)
  }

  const CustomRow = useCallback(
    (props: any) =>
      isMobile ? (
        <MobileClientRow
          {...props.row}
          onRowClick={handleRowClick}
        />
      ) : (
        <GridRow {...props} />
      ),
    [isMobile]
  )

  const CustomToolbar = useCallback(() => {
    return (
      <GridToolbarContainer
        sx={({ palette }) => ({
          p: 2,
          borderBottom: '2px solid',
          borderBottomColor: palette.divider,
          gap: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
        })}>
        <GridToolbarQuickFilter
          variant="outlined"
          size="small"
          placeholder={LABELS.SEARCH}
          sx={{
            minWidth: isMobile ? '100%' : 200,
          }}
        />
        {!isMobile && <GridToolbarDensitySelector />}
        <Box sx={{ flexGrow: 1 }} />
      </GridToolbarContainer>
    )
  }, [isMobile])

  useEffect(() => {
    return () => client.reset()
  }, [])

  return (
    <PageWrapper
      pageTitle={LABELS.CLIENTS}
      allowed={policies.can(POLICIES.CLIENT.READ)}
      allowedMessage={WARNINGS.NOT_ALLOWED.NO_COMPANY}
      actions={
        <Tooltip title={TOOLTIPS.CREATE_NEW_CLIENT}>
          <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
            <Button
              size="small"
              endIcon={<Add />}
              disabled={!canCreate}
              variant="contained"
              onClick={handleCreateClient}
              sx={{ width: isMobile ? '100%' : 'auto' }}>
              Nieuw
            </Button>
          </Box>
        </Tooltip>
      }>
      <OvDataGrid
        onRowClick={handleRowClick}
        rowSelectionModel={rowSelectionModel}
        store={client.data_grid}
        slots={{
          row: CustomRow,
          toolbar: CustomToolbar,
        }}
        withToolBar={true}
        onRowSelectionModelChange={(model, details) => setRowSelectionModel(model)}
        responsiveColumns
      />
    </PageWrapper>
  )
})
